import { useEffect, useState, useCallback } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ChartBar } from "@phosphor-icons/react";
import LoadFailedPanel from "@/components/LoadFailedPanel";

export default function AuditTrailDashboard() {
  const { user } = useAuth();
  const can = (user?.ux_tier || 0) >= 5;
  const [d, setD] = useState(null);
  const [days, setDays] = useState(90);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    if (!can) return;
    setErr(null);
    setD(null);
    api.get(`/admin/audit-trail?days=${days}`).then(r => setD(r.data)).catch(e => setErr(e));
  }, [can, days]);

  useEffect(() => { load(); }, [load]);

  if (!can) return <div className="p-6 text-[var(--text-secondary)]">CEO only.</div>;
  if (err) return <div className="p-6"><LoadFailedPanel error={err} resource="the Audit-Trail dashboard" onRetry={load} /></div>;
  if (!d) return <div className="p-6 text-[var(--text-muted)]">Loading audit trail…</div>;

  const maxMonth = Math.max(1, ...d.by_month.map(m => m.count));

  return (
    <div data-testid="audit-trail-page" className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#263776] flex items-center gap-2">
            <ChartBar size={28} className="text-[#F47D20]" />
            Audit-Trail Dashboard
          </h1>
          <div className="text-xs text-[var(--text-muted)]">Deletion requests grouped by tier, department, period · last {d.window_days} days</div>
        </div>
        <div className="flex gap-2">
          {[30, 90, 180, 365].map(n => (
            <button key={n} data-testid={`audit-days-${n}`} onClick={() => setDays(n)}
              className={`px-3 py-1 text-xs rounded transition ${days === n ? "bg-[#263776] text-white" : "bg-white border border-[var(--border)] text-[var(--text-secondary)]"}`}>
              {n}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[var(--border)] rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)]">Total requests</div>
          <div className="text-3xl font-bold text-[#263776]" data-testid="audit-total">{d.total}</div>
        </div>
        <div className="bg-white border border-[var(--border)] rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)]">Approved</div>
          <div className="text-3xl font-bold text-emerald-600" data-testid="audit-approved">{d.by_status?.approved || 0}</div>
        </div>
        <div className="bg-white border border-[var(--border)] rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)]">Rejected</div>
          <div className="text-3xl font-bold text-rose-600" data-testid="audit-rejected">{d.by_status?.rejected || 0}</div>
        </div>
        <div className="bg-white border border-[var(--border)] rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)]">Median cycle (h)</div>
          <div className="text-3xl font-bold text-[#263776]" data-testid="audit-median">{d.median_approval_hours ?? "—"}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section data-testid="audit-by-tier" className="bg-white border border-[var(--border)] rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-3">By requester tier</div>
          {Object.keys(d.by_tier).length === 0 ? <div className="text-xs text-[var(--text-faint)]">No data.</div> :
            Object.entries(d.by_tier).sort().map(([t, n]) => (
              <div key={t} className="flex items-center gap-3 py-1 text-sm">
                <span className="w-16 text-[var(--text-muted)]">Tier {t}</span>
                <div className="flex-1 bg-[var(--surface-3)] rounded h-3"><div className="bg-[#F47D20] h-3 rounded" style={{width: `${Math.min(100, n / Math.max(1, ...Object.values(d.by_tier)) * 100)}%`}} /></div>
                <span className="w-8 text-right font-mono">{n}</span>
              </div>
            ))}
        </section>

        <section data-testid="audit-by-status" className="bg-white border border-[var(--border)] rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-3">By status</div>
          {Object.keys(d.by_status).length === 0 ? <div className="text-xs text-[var(--text-faint)]">No data.</div> :
            Object.entries(d.by_status).map(([s, n]) => (
              <div key={s} className="flex items-center gap-3 py-1 text-sm">
                <span className="w-24 text-[var(--text-muted)] capitalize">{s}</span>
                <div className="flex-1 bg-[var(--surface-3)] rounded h-3"><div className="bg-[#263776] h-3 rounded" style={{width: `${Math.min(100, n / Math.max(1, ...Object.values(d.by_status)) * 100)}%`}} /></div>
                <span className="w-8 text-right font-mono">{n}</span>
              </div>
            ))}
        </section>

        <section data-testid="audit-by-dept" className="bg-white border border-[var(--border)] rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-3">By department</div>
          {d.by_department.length === 0 ? <div className="text-xs text-[var(--text-faint)]">No data.</div> :
            d.by_department.slice(0, 8).map(r => (
              <div key={r.department} className="flex items-center gap-3 py-1 text-sm">
                <span className="w-32 text-[var(--text-muted)] truncate">{r.department}</span>
                <div className="flex-1 bg-[var(--surface-3)] rounded h-3"><div className="bg-emerald-500 h-3 rounded" style={{width: `${Math.min(100, r.count / Math.max(1, ...d.by_department.map(x => x.count)) * 100)}%`}} /></div>
                <span className="w-8 text-right font-mono">{r.count}</span>
              </div>
            ))}
        </section>

        <section data-testid="audit-by-resource" className="bg-white border border-[var(--border)] rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-3">By resource type</div>
          {d.by_resource_type.length === 0 ? <div className="text-xs text-[var(--text-faint)]">No data.</div> :
            d.by_resource_type.map(r => (
              <div key={r.resource_type} className="flex items-center gap-3 py-1 text-sm">
                <span className="w-24 text-[var(--text-muted)]">{r.resource_type}</span>
                <div className="flex-1 bg-[var(--surface-3)] rounded h-3"><div className="bg-sky-500 h-3 rounded" style={{width: `${Math.min(100, r.count / Math.max(1, ...d.by_resource_type.map(x => x.count)) * 100)}%`}} /></div>
                <span className="w-8 text-right font-mono">{r.count}</span>
              </div>
            ))}
        </section>
      </div>

      {d.by_month.length > 0 && (
        <section data-testid="audit-by-month" className="bg-white border border-[var(--border)] rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-3">By month (last 12 months)</div>
          <div className="flex items-end gap-2 h-32 overflow-x-auto">
            {d.by_month.map(m => (
              <div key={m.month} className="flex flex-col items-center gap-1 w-12 shrink-0">
                <div className="text-[10px] text-[var(--text-muted)]">{m.count}</div>
                <div className="w-8 bg-[#F47D20] rounded-t" style={{height: `${(m.count / maxMonth) * 90 + 4}px`}} />
                <div className="text-[9px] text-[var(--text-faint)] font-mono">{m.month}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section data-testid="audit-recent" className="bg-white border border-[var(--border)] rounded-lg overflow-hidden">
        <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] px-5 py-3 border-b border-[var(--border)]">Most recent 25 requests</div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-2)] text-[var(--text-muted)] text-xs">
            <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Resource</th><th className="text-left px-3 py-2">Requester</th><th className="text-left px-3 py-2">Status</th></tr>
          </thead>
          <tbody>
            {d.recent.map(r => (
              <tr key={r.request_id} data-testid={`audit-recent-${r.request_id}`} className="border-t border-[var(--border)]">
                <td className="px-3 py-2 text-xs text-[var(--text-muted)]">{r.created_at?.slice(0, 16)}</td>
                <td className="px-3 py-2 text-xs">{r.resource_type} · <span className="font-mono">{(r.resource_id || "").slice(-12)}</span></td>
                <td className="px-3 py-2 text-xs">{r.requester_name} · T{r.requester_tier}</td>
                <td className="px-3 py-2 text-xs capitalize">{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
